import { useState } from "react";
import { useDebounce } from "../../services/useDebounce";
import { useQuery } from "@tanstack/react-query";
import { ReactComponent as SearchIcon } from "../../assets/icons/search.svg";
import { ReactComponent as ChevronDownIcon } from "../../assets/icons/chevron-down.svg";
import { NotificationButton } from "../../components/NotificationButton";
import { LottieLoading } from "../../components/Loading/LottieLoading";
import { Button } from "../../components/Button";
import { Card } from "../../components/Card";
import { PdfFrame } from "../../components/PdfViewer";
import SideModal from "../certifications/components/SideModal";
import apiClient from "../../services/axios";
import { api } from "../../services/api";

interface FaqDocument {
  id: string;
  title: string;
  s3_key: string;
  page_count?: number;
  updated_at?: string;
}

interface FaqCategory {
  slug: string;
  label: string;
  sort_order: number;
}

interface FaqItem {
  id: string;
  question: string;
  answer: string;
  category_slug: string;
  sort_order: number;
}

interface FaqAssets {
  documents?: FaqDocument[];
  categories?: FaqCategory[];
  items?: FaqItem[];
}

function DocumentCard({ doc, onView }: { doc: FaqDocument; onView: () => void }) {
  const fileLine = ["PDF", doc.page_count ? `${doc.page_count} Pages` : null]
    .filter(Boolean)
    .join(" - ");

  return (
    <Card className="!px-[18px] !py-[13px] w-fit min-w-[260px]">
      <div className="text-black text-base font-semibold mb-1">{doc.title}</div>
      <div className="text-black text-xs mb-1">{fileLine}</div>
      {doc.updated_at && (
        <div className="text-black text-xs mb-4">Updated {doc.updated_at}</div>
      )}
      <Button
        onClick={onView}
        variant="primary"
        className="!py-1.5 !px-4 !rounded-full !text-xs !h-auto !bg-[var(--menuSecondaryBg)]"
      >
        View
      </Button>
    </Card>
  );
}

function AccordionItem({ item, open, onToggle }: { item: FaqItem; open: boolean; onToggle: () => void }) {
  return (
    <div>
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center justify-between px-3 py-[10px] text-left"
      >
        <span className="text-black text-sm font-normal">{item.question}</span>
        <ChevronDownIcon
          className={`w-6 h-6 text-black flex-shrink-0 transition-transform duration-200 ${open ? "rotate-180" : ""}`}
        />
      </button>
      <div className={`grid transition-[grid-template-rows] duration-200 ${open ? "grid-rows-[1fr]" : "grid-rows-[0fr]"}`}>
        <div className="overflow-hidden">
          <div className="px-3 py-3 text-black text-xs leading-relaxed bg-background2">{item.answer}</div>
        </div>
      </div>
    </div>
  );
}

export function FAQs() {
  const [search, setSearch] = useState("");
  const [activeCategory, setActiveCategory] = useState("all");
  const [openItemId, setOpenItemId] = useState<string | null>(null);
  const [selectedDoc, setSelectedDoc] = useState<FaqDocument | null>(null);
  const [docModalOpen, setDocModalOpen] = useState(false);
  // The side panel opens with a width animation (w-0 → w-[50vw]). We defer
  // mounting the PDF iframe until that transition completes, so Chrome's PDF
  // viewer fits (and persists) its zoom to the final full width rather than a
  // transitional narrow width. Loading at a narrow width caused the preview to
  // progressively shrink across repeated open/reopen cycles.
  const [panelReady, setPanelReady] = useState(false);

  const openDoc = (doc: FaqDocument) => { setSelectedDoc(doc); setPanelReady(false); setDocModalOpen(true); };
  const closeDoc = () => setDocModalOpen(false);

  const debouncedSearch = useDebounce(search, 300);

  const { data, isLoading, isFetching, isError: assetsError } = useQuery({
    queryKey: ["faq-assets", debouncedSearch, activeCategory],
    queryFn: () => api.getFaqAssets({
      search: debouncedSearch || undefined,
      category: activeCategory !== "all" ? activeCategory : undefined,
    }),
    keepPreviousData: true,
  });

  const faq: FaqAssets = data?.data ?? {};
  const documents = faq.documents ?? [];
  const categories = faq.categories ?? [];
  const items = faq.items ?? [];

  const {
    data: docSignedUrl,
    isLoading: docLoading,
    isError: docError,
  } = useQuery({
    queryKey: ["faq-doc-url", selectedDoc?.s3_key ?? ""],
    queryFn: async () => {
      const res = await apiClient.get<{ url: string }>(`assets/files/${selectedDoc!.s3_key}`);
      return res.data.url;
    },
    enabled: !!selectedDoc?.s3_key && docModalOpen,
    staleTime: 4 * 60 * 1000,
    cacheTime: 5 * 60 * 1000,
    refetchInterval: 4 * 60 * 1000,
  });

  return (
    <div className="p-6 pb-10 flex flex-col gap-4 min-h-full">
      <div className="flex items-start justify-between">
        <div className="flex flex-col gap-1">
          <div className="text-black text-[34px] font-bold leading-tight">FAQs</div>
          <div className="text-gray3 text-base font-normal">
            Answers to common questions about your account and features.
          </div>
        </div>
        <NotificationButton />
      </div>

      {assetsError && !data ? (
        <div className="flex-1 flex justify-center items-center">
          <div className="text-black text-sm">Failed to load FAQs. Please try again later.</div>
        </div>
      ) : isLoading ? (
        <div className="flex-1 flex justify-center items-center">
          <LottieLoading />
        </div>
      ) : (
        <div className={`flex flex-col gap-4 transition-opacity duration-150 ${isFetching && !isLoading ? "opacity-50 pointer-events-none" : ""}`}>
          {documents.length > 0 && (
            <div className="flex gap-4 flex-wrap">
              {documents.map((doc) => (
                <DocumentCard
                  key={doc.id}
                  doc={doc}
                  onView={() => openDoc(doc)}
                />
              ))}
            </div>
          )}

          <div className="flex items-center gap-2 border border-[#ececec] rounded-lg px-4 py-2 bg-white">
            <SearchIcon className="w-5 h-5 text-gray3 flex-shrink-0" />
            <input
              type="text"
              value={search}
              onChange={(e) => { setSearch(e.target.value); setOpenItemId(null); }}
              placeholder="Search FAQs..."
              className="flex-1 text-sm text-black placeholder-gray3 outline-none bg-transparent"
            />
          </div>

          {categories.length > 0 && (
            <div className="flex gap-2 flex-wrap">
              <button
                type="button"
                onClick={() => { setActiveCategory("all"); setOpenItemId(null); }}
                className={`px-4 py-1.5 rounded-full text-xs font-medium transition-colors ${
                  activeCategory === "all"
                    ? "bg-[var(--menuSecondaryBg)] text-white border border-transparent"
                    : "bg-white border border-[#ececec] text-black hover:border-primary"
                }`}
              >
                All
              </button>
              {categories.map((cat) => (
                <button
                  key={cat.slug}
                  type="button"
                  onClick={() => { setActiveCategory(cat.slug); setOpenItemId(null); }}
                  className={`px-4 py-1.5 rounded-full text-xs font-medium transition-colors ${
                    activeCategory === cat.slug
                      ? "bg-[var(--menuSecondaryBg)] text-white border border-transparent"
                      : "bg-white border border-[#ececec] text-black hover:border-primary"
                  }`}
                >
                  {cat.label}
                </button>
              ))}
            </div>
          )}

          {items.length > 0 && (
            <div className="flex flex-col gap-2">
              {items.map((item) => (
                <div key={item.id} className="border border-[#ececec] rounded-lg bg-white overflow-hidden">
                  <AccordionItem
                    item={item}
                    open={openItemId === item.id}
                    onToggle={() => setOpenItemId(openItemId === item.id ? null : item.id)}
                  />
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <SideModal
        show={docModalOpen}
        toggle={closeDoc}
        title={selectedDoc?.title ?? ""}
        showHeader={false}
        containerClass="!p-0 flex flex-col"
        onOpened={() => setPanelReady(true)}
      >
        <PdfFrame
          src={panelReady ? docSignedUrl ?? "" : ""}
          displayName={selectedDoc?.title ?? ""}
          loading={docLoading || !panelReady}
          error={docError && !docSignedUrl}
          onClose={closeDoc}
          fillContainer
        />
      </SideModal>
    </div>
  );
}
